<template>
  <div>
    <StackedHorizontalBarChart
      :title="$t('activityByTeam')"
      :filterContext="filterContext"
      :dataProviders="dataProviders"
      :contextUpdaters="contextUpdaters"
      :navigationConfig="navigationConfig"
      :chartFontColor="chartFontColor"
      @updateFilter="handleFilterUpdate"
      :currentLevel="currentLevel"
      :dataLoaded="dataLoaded"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import StackedHorizontalBarChart from '@/components/charts/base/StackedHorizontalBarChart.vue';
import { ensureChartUsersTable } from '@/utils/chartUsers';

const props = defineProps({
  filterContext: {
    type: Object,
    required: true
  },
  dataLoaded: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['updateFilter']);

const store = useStore();
const { t } = useI18n();
const chartFontColor = computed(() => store.getters.chartFontColor);
const conn = store.getters.getDBConnection;

const chartName = 'statusHistoryTeamsStackedBarChart';
const currentLevel = ref(0);

const getTeamProfileData = async () => {
  const query = `
    SELECT 
      t.teamProfileId AS id,
      t.teamProfileId AS parentId,
      t.teamProfileTitle AS title,
      s.title AS status_title,
      s.color AS color,
      COUNT(*) AS value,
      MIN(s.sort) as sort
    FROM filteredStatusHistory sh
    JOIN teams t ON sh.createdByTeamId = t.id
    JOIN status_templates s ON sh.statusId = s.id
    GROUP BY t.teamProfileId, t.teamProfileTitle, s.title, s.color
    ORDER BY t.teamProfileTitle, sort, s.title
  `;
  
  return executeAndProcessQuery(query);
};

const getTeamData = async () => {
  const query = `
    SELECT 
      t.id AS id,
      t.teamProfileId AS parentId,
      t.name AS title,
      s.title AS status_title,
      s.color AS color,
      COUNT(*) AS value,
      MIN(s.sort) as sort
    FROM filteredStatusHistory sh
    JOIN teams t ON sh.createdByTeamId = t.id
    JOIN status_templates s ON sh.statusId = s.id
    GROUP BY t.id, t.teamProfileId, t.name, s.title, s.color
    ORDER BY t.name, sort, s.title
  `;
  
  return executeAndProcessQuery(query);
};

const getUserData = async () => {
  // The users table is a Key/Value store and cannot be joined directly;
  // use the chart_users lookup built from team_users instead
  await ensureChartUsersTable(conn);
  const query = `
    SELECT
      sh.createdByUserId AS id,
      sh.createdByTeamId AS parentId,
      COALESCE(u.displayName, sh.createdByUserId) AS title,
      s.title AS status_title,
      s.color AS color,
      COUNT(*) AS value,
      MIN(s.sort) as sort
    FROM filteredStatusHistory sh
    LEFT JOIN chart_users u ON sh.createdByUserId = u.id
    JOIN status_templates s ON sh.statusId = s.id
    GROUP BY sh.createdByUserId, sh.createdByTeamId, u.displayName, s.title, s.color
    ORDER BY title, sort, s.title
  `;

  return executeAndProcessQuery(query);
};

const getStatusData = async () => {
  const query = `
    SELECT
      s.id AS id,
      sh.createdByUserId AS parentId,
      s.title AS title,
      s.title AS status_title,
      s.color AS color,
      COUNT(*) AS value,
      MIN(s.sort) as sort
    FROM filteredStatusHistory sh
    JOIN status_templates s ON sh.statusId = s.id
    GROUP BY s.id, sh.createdByUserId, s.title, s.color, s.sort
    ORDER BY s.sort, s.title
  `;

  return executeAndProcessQuery(query);
};

const getSingleStatusData = async () => {
  const query = `
    SELECT
      s.id AS id,
      s.title AS title,
      s.title AS status_title,
      s.color AS color,
      COUNT(*) AS value,
      MIN(s.sort) as sort
    FROM filteredStatusHistory sh
    JOIN status_templates s ON sh.statusId = s.id
    GROUP BY s.id, s.title, s.color, s.sort
    ORDER BY s.sort, s.title
  `;

  return executeAndProcessQuery(query);
};

const getTeamProfileFilterContext = async (teamProfileId) => {
  const query = `
    SELECT DISTINCT sh.subItemId, sh.validFrom, sh.statusId
    FROM status_history_temporal sh
    JOIN teams t ON sh.createdByTeamId = t.id
    WHERE t.teamProfileId = '${teamProfileId}'
  `;
  
  await updateFilteredItemsContext(query);
};

const getTeamFilterContext = async (teamId) => {
  const query = `
    SELECT DISTINCT sh.subItemId, sh.validFrom, sh.statusId
    FROM status_history_temporal sh
    WHERE sh.createdByTeamId = '${teamId}'
  `;
  
  await updateFilteredItemsContext(query);
};

const getUserFilterContext = async (userId, teamId) => {
  const query = `
    SELECT DISTINCT sh.subItemId, sh.validFrom, sh.statusId
    FROM status_history_temporal sh
    WHERE sh.createdByUserId = '${userId}' AND sh.createdByTeamId = '${teamId}'
  `;

  await updateFilteredItemsContext(query);
};

const getStatusFilterContext = async (statusId, userId) => {
  const query = `
    SELECT DISTINCT sh.subItemId, sh.validFrom, sh.statusId
    FROM status_history_temporal sh
    WHERE sh.statusId = '${statusId}' AND sh.createdByUserId = '${userId}'
  `;

  await updateFilteredItemsContext(query);
};

const resetFilterContext = async () => {
  await props.filterContext.removeFilter(chartName);
};

const dataProviders = {
  teamProfile: getTeamProfileData,
  team: getTeamData,
  user: getUserData,
  status: getStatusData,
  singlestatus: getSingleStatusData
};

const contextUpdaters = {
  teamProfile: getTeamProfileFilterContext,
  team: getTeamFilterContext,
  user: getUserFilterContext,
  status: getStatusFilterContext,
  reset: resetFilterContext
};

const navigationConfig = [
  { level: 'teamProfile', titleKey: 'teamProfile' },
  { level: 'team', titleKey: 'teams' },
  { level: 'user', titleKey: 'users' },
  { level: 'status', titleKey: 'status' },
  { level: 'singlestatus', titleKey: 'singlestatus' }
];

const updateFilteredItemsContext = async (query) => {
  await props.filterContext.updateFilterWithQuery(chartName, query);
};

const handleFilterUpdate = (chartId, filteredContext) => {
  emit('updateFilter', chartId, filteredContext);
};

// Shared data processing utility function
const processChartData = (rows) => {
  const groupedData = {};

  rows.forEach(row => {
    const id = row['id'];
    const title = row['title'];
    const parentId = row['parentId'] ? row['parentId'] : null;
    
    if (!groupedData[id]) {
      groupedData[id] = {
        name: title,
        id: id,
        parentId: parentId,
        children: []
      };
    }
    
    // Find if this color already exists in the children
    const existingColor = groupedData[id].children.find(
      child => child.color === row.color
    );
    
    if (existingColor) {
      existingColor.value += row.value;
      // Store the status names by color
      if (!existingColor.statusCounts) {
        existingColor.statusCounts = {};
      }
      existingColor.statusCounts[row.status_title] = row.value;
    } else {
      groupedData[id].children.push({
        color: row.color,
        value: row.value,
        statusName: row.status_title,
        sort: row.sort,
        statusCounts: { [row.status_title]: row.value }
      });
    }
  });

  return Object.values(groupedData).map(item => ({
    ...item,
    children: item.children.sort((a, b) => a.sort - b.sort)
  }));
};

// Generic query execution function
const executeAndProcessQuery = async (query) => {
  const result = await conn.query(query);
  const rows = result.toArray().map((row) => {
    const jsonRow = row.toJSON();
    return {
      ...jsonRow,
      value: Number(jsonRow.value)
    };
  });

  return processChartData(rows);
};
</script>